import * as React from "react";
import {
  DatePicker as MSDatePicker,
  DayOfWeek,
  mergeStyleSets,
  Label,
} from "@fluentui/react";
import { useField, useFormikContext } from "formik";
import { parse, format } from "date-fns";

const DayPickerStrings = {
  months: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ],

  shortMonths: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ],

  days: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ],

  shortDays: ["S", "M", "T", "W", "T", "F", "S"],

  goToToday: "Go to today",
  prevMonthAriaLabel: "Go to previous month",
  nextMonthAriaLabel: "Go to next month",
  prevYearAriaLabel: "Go to previous year",
  nextYearAriaLabel: "Go to next year",
  closeButtonAriaLabel: "Close date picker",
  monthPickerHeaderAriaLabel: "{0}, select to change the year",
  yearPickerHeaderAriaLabel: "{0}, select to change the month",

  isRequiredErrorMessage: "Start date is required.",

  invalidInputErrorMessage: "Invalid date format.",
};

const controlClass = mergeStyleSets({
  control: {
    margin: "0 0 15px 0",
    maxWidth: "300px",
  },
});

const controlClassMullti = mergeStyleSets({
  control: { maxWidth: "250px", minWidth: "100px" },
});

const firstDayOfWeek = DayOfWeek.Sunday;
const desc =
  "This field is required. One of the support input formats is year dash month dash day.";

export const DatePicker = (props) => {
  // eslint-disable-next-line no-unused-vars
  const [field, _meta, helpers] = useField(props);

  const onSelectDate = (date) => {
    helpers.setValue(date);
    helpers.setTouched(true);
  };

  return (
    <MSDatePicker
      {...field}
      className={controlClass.control}
      ariaLabel={desc}
      firstDayOfWeek={firstDayOfWeek}
      strings={DayPickerStrings}
      value={
        typeof field.value === "string" ? new Date(field.value) : field.value
      }
      onSelectDate={onSelectDate}
      textField={props.name}
      {...props}
    />
  );
};

export const MultiDatePicker = ({ name, label, formatStr, required = false, errorMessage = "This field is required." }) => {
  const { values, setFieldValue, errors, touched } = useFormikContext();
  const [field, meta] = useField(name);
  const dates = field.value || [];

  const parseDate = (dateString) => {
    if (!dateString) return null;
    return parse(dateString + "/" + new Date().getFullYear(), "MM/dd/yyyy", new Date());
  };

  const onSelectDate = (date, index) => {
    if (date) {
      const formattedDate = format(date, formatStr);
      const newDates = [...dates];
      newDates[index] = formattedDate;
      setFieldValue(name, newDates);
    }
  };

  const addNewDate = () => {
    setFieldValue(name, [...dates, ""]);
  };

  const removeDate = (index) => {
    const newDates = dates.filter((_, i) => i !== index);
    setFieldValue(name, newDates);
  };

  const hasError = required && touched[name] && (!dates.length || dates.some(date => !date));

  return (
    <div>
      <Label>
        {label} {required && "*"}
      </Label>
      <button 
        type="button" 
        onClick={addNewDate}
        style={{ fontSize: '14px', fontWeight: 400 }}
      >
        Add Date
      </button>
      {dates.map((date, index) => (
        <div key={index} style={{ display: "flex", gap: "20px", marginTop: "15px", alignItems: "baseline" }}>
          <MSDatePicker
            className={`${controlClassMullti.control} ${hasError ? "error" : ""}`}
            ariaLabel="Select a date"
            placeholder={formatStr}
            value={parseDate(date)}
            onSelectDate={(d) => onSelectDate(d, index)}
            formatDate={(d) => (d ? format(d, formatStr) : "")}
          />
          <button 
            type="button" 
            onClick={() => removeDate(index)}
            style={{ background: "transparent", borderWidth: 0 }}
          >
            ❌
          </button>
        </div>
      ))}
      {hasError && <div style={{ color: "red", marginTop: "5px" }}>{errorMessage}</div>}
    </div>
  );
};

